import { readAgentVersion } from "../runner/session-runner-helpers.js";
import { reportBootMilestone } from "../setup/boot-milestone.js";
import { oomWatchdogOptionsFromEnv } from "../workbench/oom-watchdog.js";
import { workbenchPort } from "../workbench/mode.js";
import { DEFAULT_WORKBENCH_PORT, type GitStatusFrame } from "../workbench/protocol.js";
import { GitPrepJob, type GitPrepState } from "./git-prep.js";
import type { BootContext } from "./types.js";
import { defaultWorkbenchBootDeps, type WorkbenchBootDeps } from "./workbench-boot-deps.js";

/**
 * The workbench container's vnext boot: acquire the bundle, start the daemon
 * (whose listen socket IS the pod's startupProbe), then run everything else —
 * git prep, user-home wiring, sshd, ownership reclaim — strictly after the
 * probe can pass. Replaces the bash entrypoint's workbench tail
 * (entrypoint.sh:835-899): the marker files become the GitPrepJob state
 * served over the daemon's `gitStatus` op.
 *
 * Task 9 composes this through runPipeline; every step is injectable via the
 * deps object (`workbench-boot-deps.ts` carries the production defaults) so
 * tests run without network, sockets, or real git.
 */

export type { WorkbenchBootDeps } from "./workbench-boot-deps.js";

export function toGitStatusFrame(state: GitPrepState): GitStatusFrame {
  return state.state === "failed"
    ? { t: "gitStatus", state: "failed", reason: state.reason }
    : { t: "gitStatus", state: state.state };
}

function warnErr(ctx: BootContext, label: string): (err: unknown) => void {
  return (err) => {
    ctx.log.warn(
      `[boot] WARN ${label} failed: ${err instanceof Error ? err.message : String(err)}`,
    );
  };
}

export async function runWorkbenchBoot(
  ctx: BootContext,
  overrides: Partial<WorkbenchBootDeps> = {},
): Promise<never> {
  const deps: WorkbenchBootDeps = { ...defaultWorkbenchBootDeps(), ...overrides };
  // ── CRITICAL PATH ──────────────────────────────────────────────────────
  // The daemon's listen socket is the pod's startupProbe — it gates both the
  // agent container start and Provisioning→Running. Nothing may run before
  // it besides bundle acquisition and env unpack.
  //
  // The poll's 401 (BootstrapUnauthorizedError) propagates — fatal-loud.
  const bundle = await deps.pollBundle(ctx);
  if (!bundle) {
    // Aborted during standby (SIGTERM). Task 9's signal wiring owns process
    // exit; parking preserves the Promise<never> contract meanwhile.
    ctx.log.info("[boot] standby poll aborted — parking for shutdown");
    return deps.park(ctx);
  }
  ctx.bundle = bundle;
  ctx.childEnv = deps.unpack(ctx, bundle);
  const job = new GitPrepJob(deps.gitPrepDeps(ctx, bundle), {
    // Order pinned by the old entrypoint-grimoire test: binds BEFORE the
    // ready flip (Claude must not spawn before skills exist), references after.
    onReady: () => {
      // Must still return this promise — GitPrepJob awaits onReady() before
      // flipping status to "ready" (the daemon's gitStatus gate), so binds
      // completing before the ready flip is preserved. The milestone report
      // is fire-and-forget and never delays that flip.
      const binds = deps.bindsBeforeReady(ctx, bundle);
      void reportBootMilestone({ key: "repo_synced" });
      return binds;
    },
    afterReady: () => deps.cloneReferences(ctx, bundle),
  });
  await deps.startDaemon({
    port: workbenchPort() ?? DEFAULT_WORKBENCH_PORT,
    token: ctx.bootstrapToken,
    version: readAgentVersion() ?? "unknown",
    getGitStatus: () => toGitStatusFrame(job.status),
    oomWatchdog: oomWatchdogOptionsFromEnv(ctx.log),
  });
  ctx.log.info("[boot] step=startDaemon listen socket up (startupProbe gate open)");
  void reportBootMilestone({ key: "workbench_ready" });

  // ── DEFERRED (post-listen) ─────────────────────────────────────────────
  // Everything below runs after the probe can pass; each degrades alone.
  // Ensure the repo cwd exists BEFORE the git prep job — the no-git-plan path
  // returns ready without creating it, and the daemon serves shell cwd from here.
  try {
    deps.ensureWorkspaceDir(ctx);
  } catch (err) {
    warnErr(ctx, "ensureWorkspaceDir")(err);
  }
  job.start();
  try {
    deps.wireHome(ctx);
  } catch (err) {
    warnErr(ctx, "wireUserHome")(err);
  }
  void deps.startSshd(ctx).catch(warnErr(ctx, "startWorkspaceSshd"));
  void deps.reclaimOwnership(ctx).catch(warnErr(ctx, "reclaimHomeOwnership"));
  return deps.park(ctx);
}
